import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import axiosInstance, { publicInstance } from '../axiosInstance';
import { useRefresh } from '../../Components/RefreshContext'; 
import { useAtom } from 'jotai';
import { serviceCommentsCountAtom } from '../../helpers/atoms';
import ReportIssueDialog from '../../Components/Dialogs/ReportIssueDialog';
import ReportRepairDialog from '../../Components/Dialogs/ReportRepairDialog';
import { useTheme } from '@mui/material/styles';

interface ServiceComment {
  comment: string;
  username: string;
  id: number;
  time_stamp: string;
  box: number;
  status: string;
}

interface ServiceCommentsProps {
  deviceNumber: string;
}

const ServiceComments: React.FC<ServiceCommentsProps> = ({ deviceNumber }) => {
  const [comments, setComments] = useState<ServiceComment[]>([]);
  const [open, setOpen] = useState(false);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const { refresh } = useRefresh(); 
  const [, setServiceCommentsCount] = useAtom(serviceCommentsCountAtom);
  const theme = useTheme();

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await publicInstance.get(`/dacapi_sensorbox_service_comments?box=eq.${deviceNumber}`);
        const sortedComments = response.data.sort((a: ServiceComment, b: ServiceComment) => new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime());
        setComments(sortedComments);
      } catch (error) {
        console.error('Error fetching service comments:', error);
      }
    };

    fetchComments();
  }, [deviceNumber]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleRepairDialogOpen = () => {
    setRepairDialogOpen(true);
  };

  const handleRepairDialogClose = () => {
    setRepairDialogOpen(false);
  };

  const handleIssueDialogOpen = () => {
    setIssueDialogOpen(true);
  };

  const handleIssueDialogClose = () => {
    setIssueDialogOpen(false);
  };

  const handleRepairSuccess = async (message: string) => {
    try {
      const userResponse = await axiosInstance.get('/auth/me');
      const username = userResponse.data.username;
      
      // Refresh comments from server
      const response = await publicInstance.get(`/dacapi_sensorbox_service_comments?box=eq.${deviceNumber}`);
      const sortedComments = response.data.sort((a: ServiceComment, b: ServiceComment) => new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime());
      setComments(sortedComments);
      
      // Update global comments count
      const allCommentsResponse = await publicInstance.get('/view_dacapi_sensorbox_service_comments');
      setServiceCommentsCount(allCommentsResponse.data.length);
      
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refresh();
    } catch (error) {
      console.error('Error refreshing comments:', error);
    }
  };

  const handleIssueSuccess = async (message: string) => {
    try {
      const userResponse = await axiosInstance.get('/auth/me');
      const username = userResponse.data.username;
      
      // Refresh comments from server
      const response = await publicInstance.get(`/dacapi_sensorbox_service_comments?box=eq.${deviceNumber}`);
      const sortedComments = response.data.sort((a: ServiceComment, b: ServiceComment) => new Date(b.time_stamp).getTime() - new Date(a.time_stamp).getTime());
      setComments(sortedComments);
      
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setServiceCommentsCount(prevCount => prevCount + 1);
      refresh();
    } catch (error) {
      console.error('Error refreshing comments:', error);
    }
  };

  const handleError = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const latestComment = comments[0];

  return (
    <Card sx={{ margin: "auto", mt: 2, border: `1px solid ${theme.palette.borders.primary}` }}>
      <CardHeader
        title="Service Comments"
        action={
          <Box display="flex" alignItems="center">
            <Tooltip title={open ? "Hide Details" : "Show Details"}>
              <IconButton onClick={handleToggle}>
                <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Report Repair">
              <IconButton onClick={handleRepairDialogOpen}>
                <BuildIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Report Issue">
              <IconButton onClick={handleIssueDialogOpen}>
                <ReportProblemIcon />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {!open && latestComment && (
          <Box>
            <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}><strong>Comment:</strong> {latestComment.comment}</Typography>
            <Typography variant="body2"><strong>User:</strong> {latestComment.username}</Typography>
            <Typography variant="body2"><strong>Time:</strong> {new Date(latestComment.time_stamp).toLocaleString()}</Typography>
            <Typography variant="body2"><strong>Status:</strong> <span style={{ color: latestComment.status === 'awaria' ? 'red' : 'green' }}>{latestComment.status}</span></Typography>
          </Box>
        )}
        
        <Collapse in={open}>
          <Box sx={{ maxHeight: 150, overflow: 'auto', mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: '4px' }}>Comment</TableCell>
                  <TableCell sx={{ padding: '4px' }}>User</TableCell>
                  <TableCell sx={{ padding: '4px' }}>Time</TableCell>
                  <TableCell sx={{ padding: '4px' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comments.map(comment => (
                  <TableRow key={comment.id}>
                    <TableCell sx={{ padding: '4px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{comment.comment}</TableCell>
                    <TableCell sx={{ padding: '4px' }}>{comment.username}</TableCell>
                    <TableCell sx={{ padding: '4px' }}>{new Date(comment.time_stamp).toLocaleString()}</TableCell>
                    <TableCell sx={{ padding: '4px' }}>
                      <Typography color={comment.status === 'awaria' ? 'error' : 'green'}>
                        {comment.status}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Collapse>

        <ReportRepairDialog
          open={repairDialogOpen}
          onClose={handleRepairDialogClose}
          deviceNumber={deviceNumber}
          onSuccess={handleRepairSuccess}
          onError={handleError}
        />

        <ReportIssueDialog
          open={issueDialogOpen}
          onClose={handleIssueDialogClose}
          deviceNumber={deviceNumber}
          onSuccess={handleIssueSuccess}
          onError={handleError}
        />
        
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default ServiceComments;
